;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[],\"staticRoutes\":[]}";globalThis["_sentryRewritesTunnelPath"] = "/sentry-tunnel";globalThis["_sentryTunnelRouteMode"] = "rewrite";globalThis["_sentryNextJsVersion"] = "16.4.3";import * as Sentry from '@sentry/nextjs'
import posthog from 'posthog-js'
import { IS_PROD, IS_STAGING } from 'constants/envs'
import { BOLT_ENERGIE_CARE_PARTNER_ID } from 'constants/constants'
import { getSessionStorageItem, setSessionStorageItem } from 'utils/sessionStorage'

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart

const EXCLUDE_USER_KEY = 'posthog_excluded'

/**
 * Excludes the user from PostHog tracking by setting a session storage item.
 */
const excludeUser = () => {
  setSessionStorageItem(EXCLUDE_USER_KEY, 'true')
}

const isExcludedFromPostHogBasedOnUrlParams = (): boolean => {
  const isAlreadyExcluded = getSessionStorageItem(EXCLUDE_USER_KEY) === 'true'

  if (isAlreadyExcluded) return true

  if (window && window.location) {
    const urlParams = new URLSearchParams(window.location.search)

    const isAffiliate = urlParams.get('utm_source')?.toLowerCase().includes('affiliate')
    const isSourceTypePush = urlParams.get('source_type')?.toLowerCase() === 'push'
    const hasEventId = urlParams.has('event_id')
    const isCareUser = urlParams.get('partner_id') === BOLT_ENERGIE_CARE_PARTNER_ID

    if (isAffiliate || isSourceTypePush || isCareUser || hasEventId) {
      excludeUser()
      return true
    }
  }

  return false
}

// Initialize PostHog client-side
const posthogProjectToken = process.env.NEXT_PUBLIC_POSTHOG_KEY

if (!posthogProjectToken) {
  if (!IS_PROD) {
    throw new Error(
      'NEXT_PUBLIC_POSTHOG_KEY variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once NEXT_PUBLIC_POSTHOG_KEY is configured'
    )
  }
} else {
  posthog.init(posthogProjectToken, {
    api_host: '/thunderbolt', // Should always be the same as the rewrites in next.config.js
    defaults: '2025-05-24',
    person_profiles: 'always',
    // capture_exceptions: true, // This enables capturing exceptions using Error Tracking
    debug: !IS_PROD && process.env.NEXT_PUBLIC_POSTHOG_DEBUG === 'true',
    loaded: (posthog) => {
      const shouldBeExcluded = isExcludedFromPostHogBasedOnUrlParams()
      const prodOrSpecificDevOnly = IS_PROD || (!IS_PROD && process.env.NEXT_PUBLIC_POSTHOG_DEV_ENABLED === 'true')

      if (shouldBeExcluded || !prodOrSpecificDevOnly) {
        posthog.opt_out_capturing()
        posthog.set_config({
          disable_web_experiments: true,
          capture_pageview: false,
          capture_pageleave: false,
          disable_session_recording: true,
          disable_surveys: true,
          disable_conversations: true
        })
      }
    },
    before_send: (event) => {
      if (event) event.properties['_instance'] = 'website'
      return event
    }
  })
}

const TO_IGNORE_ERRORS: (string | RegExp)[] = [
  /ResizeObserver loop/,
  'Non-Error promise rejection',
  /ct.pinterest.com/,
  /runtime.sendMessage/,
  /\(evaluating 'window\./,
  /__firefox__/,
  /Handling is disabled/,
  /Error invoking postMessage/,
  /Java object is gone/,
  /Blocked a frame with origin/,
  /Invariant: attempted to hard navigate to the same URL/,
  /\bUET\b/, // Bing - match on exact word (case-sensitive by default)
  /googletagmanager/,
  /google-analytics/,
  /googlesyndication/
]

// Ignore errors originating from third-party scripts we do not control
const DENY_URLS: (string | RegExp)[] = [
  /extensions\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /frame_ant/i, // Injected by an ad blocker
  /\/gtm-gtg\//i, // Google's server-side tagging container, served from our own domain
  /otSDKStub/i, // OneTrust cookie banner SDK, loaded from cdn.cookielaw.org
  /\/uc\.js/i, // Usercentrics consent SDK
  /\/cc\.js/i // Cookiebot consent SDK
]

// Find Axios ECONNABORTED (an in-flight cancelled request by the browser)
const isAbortedRequest = (exception: unknown): boolean =>
  exception instanceof Error && 'code' in exception && exception.code === 'ECONNABORTED' && exception.message === 'Request aborted'

// The Google & Chrome apps on iOS blow the stack inside their own injected scripts. WebKit can't resolve a script
// URL for those, so it attributes every frame to the document instead & the minified frame names shift on each
// deploy, which re-groups the same noise into a fresh Sentry issue every release. Our own bundle always resolves to
// /_next/, so requiring at least one frame & none of them from our bundle drops this while keeping a genuine stack
// overflow in our code visible.
const isInjectedScriptStackOverflow = (event: Sentry.ErrorEvent): boolean => {
  const exception = event.exception?.values?.[0]

  if (!exception?.value?.includes('Maximum call stack size exceeded')) return false

  const frames = exception.stacktrace?.frames ?? []

  return frames.length > 0 && !frames.some((frame) => frame.filename?.includes('/_next/'))
}

// Initialize Sentry client-side
Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: IS_PROD ? 'production' : IS_STAGING ? 'staging' : 'local',
  tracesSampleRate: IS_STAGING ? 1 : 0.1,
  normalizeDepth: 10,
  ignoreErrors: TO_IGNORE_ERRORS,
  denyUrls: DENY_URLS,
  beforeSend(event, hint) {
    if (hint.originalException instanceof Error && hint.originalException.name === 'AbortError') return null
    if (isAbortedRequest(hint.originalException)) return null
    if (isInjectedScriptStackOverflow(event)) return null
    return event
  }
})
